import React, { useEffect, useRef, useState } from 'react';
import Slider from './Slider';
import {
  MicMode,
  VoicePrefs,
  currentPrefs,
  getServerDefaults,
  savePrefs,
  sendPrefs,
} from '../voiceHud/voiceSettings';

// "Voix" card of the options: how the mic opens, how loud the player is heard and hears, and the noise
// gate. Every change is kept and sent to the game at once; the game applies it only while the voice is
// connected, so the card says so when it is not (and the choice is applied at the next connection).
const VoiceCard: React.FC = () => {
  const [prefs, setPrefs] = useState<VoicePrefs>(currentPrefs);
  const [connected, setConnected] = useState(getServerDefaults() !== null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const onReady = () => {
      setConnected(true);
      setPrefs(currentPrefs());
    };
    window.addEventListener('skymp5-client:voiceReady', onReady);
    return () => {
      window.removeEventListener('skymp5-client:voiceReady', onReady);
      window.clearTimeout(timer.current);
    };
  }, []);

  const update = (patch: Partial<VoicePrefs>) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    savePrefs(next);
    // A slider sends many values while it moves: the game gets the last one shortly after it stops
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => sendPrefs(next), 120);
  };

  const modes: Array<[MicMode, string]> = [
    ['ptt', 'Appuyer pour parler'],
    ['ptm', 'Micro ouvert, appuyer pour couper'],
  ];

  return (
    <section className="esc-card">
      <h3 className="esc-card__title">Voix</h3>

      <div className="esc-segments" role="radiogroup" aria-label="Ouverture du micro">
        {modes.map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={prefs.mode === id}
            className={'esc-segment' + (prefs.mode === id ? ' esc-segment--active' : '')}
            onClick={() => update({ mode: id })}
          >
            {label}
          </button>
        ))}
      </div>
      <p className="esc-hint">
        {prefs.mode === 'ptt'
          ? 'Le micro ne transmet que tant que tu tiens la touche de parole.'
          : 'Le micro reste ouvert ; la touche de parole le coupe et le rouvre.'}
      </p>

      <div className="esc-sliders">
        <Slider
          id="esc-voice-input"
          label="Volume du micro"
          value={prefs.inputGain}
          min={0}
          max={3}
          display={`${Math.round(prefs.inputGain * 100)} %`}
          onChange={(v) => update({ inputGain: v })}
        />
        <Slider
          id="esc-voice-output"
          label="Voix des autres"
          value={prefs.outputVolume}
          min={0}
          max={2}
          display={`${Math.round(prefs.outputVolume * 100)} %`}
          onChange={(v) => update({ outputVolume: v })}
        />
      </div>

      <div className="esc-setting">
        <div className="esc-setting__text">
          <span className="esc-setting__label" id="esc-voice-gate-label">Couper les bruits de fond</span>
          <span className="esc-setting__hint">Le micro ignore les sons plus faibles que le seuil : clavier, ventilateur, respiration.</span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={prefs.noiseGateEnabled}
          aria-labelledby="esc-voice-gate-label"
          className={'esc-switch' + (prefs.noiseGateEnabled ? ' esc-switch--on' : '')}
          onClick={() => update({ noiseGateEnabled: !prefs.noiseGateEnabled })}
        >
          <span className="esc-switch__knob" />
        </button>
      </div>

      {prefs.noiseGateEnabled ? (
        <div className="esc-sliders">
          <Slider
            id="esc-voice-gate"
            label="Seuil"
            value={prefs.noiseGateThreshold}
            min={0}
            max={0.1}
            step={0.005}
            display={`${Math.round(prefs.noiseGateThreshold * 1000) / 10} %`}
            onChange={(v) => update({ noiseGateThreshold: v })}
          />
        </div>
      ) : null}

      {!connected ? (
        <p className="esc-hint">La voix n'est pas encore connectée : tes choix sont gardés et s'appliqueront à la connexion.</p>
      ) : null}
    </section>
  );
};

export default VoiceCard;
